import { useTranslation } from 'react-i18next';
import { BarChart2, Clock, Timer, Flame } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { TaskStatusOverviewDto } from '../../types';

interface TaskStatusOverviewSectionProps {
  data: TaskStatusOverviewDto | undefined;
  isLoading: boolean;
}

interface StatusColumnProps {
  label: string;
  count: number;
  percentage: number;
  barColor: string;
  valueColor: string;
  testId: string;
}

function StatusColumn({
  label,
  count,
  percentage,
  barColor,
  valueColor,
  testId,
}: StatusColumnProps) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`text-xl font-bold ${valueColor}`}>{count}</span>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-secondary">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export function TaskStatusOverviewSection({ data, isLoading }: TaskStatusOverviewSectionProps) {
  const { t } = useTranslation('analytics');

  if (isLoading) {
    return (
      <Card data-testid="analytics-status-overview-section">
        <CardContent className="p-4 space-y-4">
          <Skeleton className="h-4 w-44" />
          <div className="grid grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-1 w-full rounded-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const todo = data?.todoTasks ?? 0;
  const inProgress = data?.inProgressTasks ?? 0;
  const done = data?.doneTasks ?? 0;
  const total = todo + inProgress + done;

  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

  return (
    <Card data-testid="analytics-status-overview-section">
      <CardContent className="p-4">
        {/* Section title */}
        <div className="mb-4 flex items-center gap-2">
          <BarChart2 className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">{t('statusOverview.title')}</h3>
        </div>

        {/* Status columns */}
        <div className="grid grid-cols-3 gap-6">
          <StatusColumn
            label={t('statusOverview.todo')}
            count={todo}
            percentage={pct(todo)}
            barColor="bg-slate-400"
            valueColor=""
            testId="analytics-status-overview-todo"
          />
          <StatusColumn
            label={t('statusOverview.inProgress')}
            count={inProgress}
            percentage={pct(inProgress)}
            barColor="bg-blue-500"
            valueColor="text-blue-600 dark:text-blue-400"
            testId="analytics-status-overview-in-progress"
          />
          <StatusColumn
            label={t('statusOverview.done')}
            count={done}
            percentage={pct(done)}
            barColor="bg-emerald-500"
            valueColor="text-emerald-600 dark:text-emerald-400"
            testId="analytics-status-overview-done"
          />
        </div>

        {/* Footer stats */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span
            className="flex items-center gap-1.5"
            data-testid="analytics-status-overview-estimated-hours"
          >
            <Clock className="h-3.5 w-3.5 shrink-0" />
            {t('statusOverview.estimatedHours', {
              value: formatHours(data?.totalEstimatedHours ?? 0),
            })}
          </span>
          <span
            className="flex items-center gap-1.5"
            data-testid="analytics-status-overview-tracked-hours"
          >
            <Timer className="h-3.5 w-3.5 shrink-0" />
            {t('statusOverview.trackedHours', { value: formatHours(data?.totalTrackedHours ?? 0) })}
          </span>
          {(data?.urgentTasksCount ?? 0) > 0 && (
            <span
              className="flex items-center gap-1.5 font-medium text-destructive"
              data-testid="analytics-status-overview-urgent"
            >
              <Flame className="h-3.5 w-3.5 shrink-0" />
              {t('statusOverview.urgentTasks', { count: data?.urgentTasksCount })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function formatHours(decimalHours: number): string {
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
